import { MediaItem } from '../../../app/services/mediaItems';
import { canonicalMediaKey, stripExtension } from './maTypes';
import { splitCsvLine } from './parseMaCsv';

/**
 * MA 表示名 CSV のパーサと照合ヘルパー。
 *
 * ショット内の CSV は「MA シーケンス CSV」（ヘッダーに media / start）と
 * 「表示名 CSV」の 2 種類があり、内容で自動判別する（isMaSequenceCsvText）。
 *
 * 表示名 CSV のフォーマット:
 * - 1 列目 = キー（RPP リージョン名 / メディアファイル名。拡張子の有無は不問）
 * - 2 列目 = 表示名（テロップ・タイムチャート CSV の label に使う）
 * - ヘッダー行は任意（どのリージョンにも一致しないため自然に無視される）
 * - `#` 行 / 空行 / 2 列目が空の行はスキップ
 */

/**
 * 照合キーの正規化（拡張子除去 + NFC + 小文字化）。
 * 表示名 CSV のキーとクリップ側の候補名の両方に適用し、
 * 拡張子の有無や macOS の NFD ファイル名による不一致を吸収する
 */
function displayNameKey(name: string): string {
  return canonicalMediaKey(stripExtension(name.trim()));
}

/** BOM 除去 + 改行分割（CRLF / CR / LF。parseMaCsv と同じ規則） */
function splitLines(text: string): string[] {
  return text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/);
}

/**
 * CSV テキストが MA シーケンス CSV かどうかの判別。
 * 最初の非コメント行（= ヘッダー行）に media と start の両方があれば
 * シーケンス CSV（parseMaCsv のヘッダー要件と同じ）、それ以外は表示名 CSV
 */
export function isMaSequenceCsvText(text: string): boolean {
  for (const line of splitLines(text)) {
    if (!line.trim()) continue;
    if (line.trimStart().startsWith('#')) continue;
    const fields = splitCsvLine(line).map(f => f.toLowerCase());
    return fields.includes('media') && fields.includes('start');
  }
  return false;
}

/**
 * 表示名 CSV のパース。キーは displayNameKey で正規化し、値は 2 列目の文字列。
 * 同じキーが複数回出た場合はファイル内の後の行が勝つ
 * （ファイル間の優先は呼び出し側が updated_at 順で解決する）
 */
export function parseMaDisplayNames(text: string): Map<string, string> {
  const names = new Map<string, string>();
  for (const line of splitLines(text)) {
    if (!line.trim()) continue;
    if (line.trimStart().startsWith('#')) continue;
    const fields = splitCsvLine(line);
    const key = fields[0] ?? '';
    const value = fields[1] ?? '';
    if (!key || !value) continue;
    names.set(displayNameKey(key), value);
  }
  return names;
}

/**
 * クリップに対応する表示名を返す（一致なしは null）。
 * 照合順: sourceName（RPP リージョン名 / CSV の media 列）
 * → audioItem.file_name（拡張子除去）→ audioItem.variation_name
 */
export function resolveMaDisplayName(
  displayNames: Map<string, string>,
  clip: { sourceName: string; audioItem: Pick<MediaItem, 'file_name' | 'variation_name'> },
): string | null {
  if (displayNames.size === 0) return null;
  const candidates = [clip.sourceName, clip.audioItem.file_name, clip.audioItem.variation_name];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const hit = displayNames.get(displayNameKey(candidate));
    if (hit !== undefined) return hit;
  }
  return null;
}
